import { z } from 'zod';
/**
 * Faults and alarms (PROJECT_SPEC §61, AFM requirement d). presence ≠ fault ≠ alarm: a fault is a detected condition,
 * an alarm is the policy decision to raise it. Codes and keys only; the UI translates (alarmTypes.<key>.*).
 */
export const AlarmSeverity = z.enum(['info', 'warning', 'critical']);
export const AlarmState = z.enum(['open', 'acknowledged', 'resolved']);
export const Alarm = z.object({
    id: z.uuid(),
    device_id: z.uuid(),
    device_name: z.string(),
    site_id: z.uuid().nullable(),
    site_name: z.string().nullable(),
    alarm_type: z.string(),
    type_name_en: z.string(),
    category: z.string(),
    severity: AlarmSeverity,
    state: AlarmState,
    opened_at: z.string(),
    last_active_at: z.string(),
    acknowledged_at: z.string().nullable(),
    acknowledged_by: z.string().nullable(),
    ack_note: z.string().nullable(),
    resolved_at: z.string().nullable(),
    resolution: z.string().nullable(),
    /** detectors that fired and their raw values (vendor data kept apart from the normalized type) */
    details: z.record(z.string(), z.unknown()),
});
/** GET /api/v1/alarms?state=active|open|acknowledged|resolved|all&severity&category&type&site_id&device_id&q&from&to&limit&before */
export const AlarmPage = z.object({
    items: z.array(Alarm),
    /** pass as `before` to get the next (older) page; null = last page */
    next: z.string().nullable(),
    total: z.number().int(),
});
/** GET /api/v1/alarms/summary?site_id */
export const AlarmSummary = z.object({
    active: z.number().int(),
    by_severity: z.record(z.string(), z.number().int()),
    by_category: z.record(z.string(), z.number().int()),
    unacknowledged: z.number().int(),
    opened_24h: z.number().int(),
    resolved_24h: z.number().int(),
    /** active alarms per type, most frequent first */
    top_types: z.array(z.object({ alarm_type: z.string(), name_en: z.string(), count: z.number().int() })),
    /** alarms opened / resolved per local day, last 30 days */
    daily: z.array(z.object({ day: z.string(), opened: z.number().int(), resolved: z.number().int() })),
});
export const AlarmAction = z.object({ note: z.string().max(1000).optional() });
export const AlarmPolicy = z.object({
    id: z.uuid(),
    site_id: z.uuid().nullable(),
    site_name: z.string().nullable(),
    enabled: z.boolean().nullable(),
    severity: AlarmSeverity.nullable(),
    params: z.record(z.string(), z.unknown()),
    can_manage: z.boolean(),
});
/** GET /api/v1/alarm-types?site_id — the normalized catalogue with its policies (Settings → Alarms). */
export const AlarmTypeItem = z.object({
    key: z.string(),
    category: z.string(),
    name_en: z.string(),
    description_en: z.string(),
    default_severity: AlarmSeverity,
    default_enabled: z.boolean(),
    params_schema: z.record(z.string(), z.unknown()),
    /** effective policy globally, or for `site_id` when given */
    effective: z.object({
        enabled: z.boolean(),
        severity: AlarmSeverity,
        params: z.record(z.string(), z.unknown()),
        source: z.string(),
    }),
    /** the global policy row (null = defaults) and the overrides on sites visible to the caller */
    global: AlarmPolicy.nullable(),
    overrides: z.array(AlarmPolicy),
    detectors: z.array(z.object({
        key: z.string(),
        protocol: z.string().nullable(),
        kind: z.string(),
        name_en: z.string(),
        enabled: z.boolean(),
    })),
    /** active faults / unresolved alarms of this type in the caller's scope */
    active_faults: z.number().int(),
    active_alarms: z.number().int(),
});
export const AlarmTypeList = z.object({
    items: z.array(AlarmTypeItem),
    can_manage_global: z.boolean(),
    /** sites (clients) on which the caller may set overrides */
    manageable_sites: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
/** PUT /api/v1/alarm-policies — upsert the policy of a type for a scope (site_id null = global). */
export const AlarmPolicyUpsert = z.object({
    alarm_type: z.string().min(1).max(64),
    site_id: z.uuid().nullable(),
    enabled: z.boolean().nullable(),
    severity: AlarmSeverity.nullable(),
    params: z.record(z.string(), z.union([z.string().max(64), z.number(), z.boolean()])).optional(),
});
/** GET /api/v1/devices/:id/faults — detected conditions (with or without an alarm). */
export const DeviceFaultList = z.object({
    items: z.array(z.object({
        id: z.uuid(),
        detector_key: z.string(),
        detector_name_en: z.string(),
        alarm_type: z.string(),
        active_since: z.string(),
        cleared_at: z.string().nullable(),
        details: z.record(z.string(), z.unknown()),
        /** whether the policy currently raises it as an alarm */
        raised: z.boolean(),
    })),
});
//# sourceMappingURL=alarms.js.map